import type { SessionSlice, GeoDataStateCreator, LayerBase, LayerStyleProperties, MapStateInfo } from './types'; // Rimosso SessionState
import { LngLatBounds } from 'maplibre-gl';

// Tipo per lo stato salvato (per maggiore chiarezza in loadSessionState)
export interface SavedState { // Aggiunto export
  version?: string;
  mapViewState?: Omit<Partial<MapStateInfo>, 'bounds'> & { bounds?: { _sw?: any; _ne?: any } };
  activeBasemapId?: string; // Modificato da activeBasemapUrl
  layers?: (LayerBase & LayerStyleProperties)[]; // Combinazione di LayerBase e stili
  openAttributeTables?: string[];
  mapControls?: {
    isFullscreenControlActive?: boolean;
    isGeolocateControlActive?: boolean;
    isNavigationControlActive?: boolean;
    isScaleControlActive?: boolean;
    isGlobeControlActive?: boolean;
  };
}

export const createSessionSlice: GeoDataStateCreator<SessionSlice> = (set, get) => {
  // Funzione di utilità per separare LayerBase e LayerStyleProperties da un oggetto combinato
  // Questo aiuta a mantenere la logica di caricamento pulita e centralizzata.
  const splitSavedLayerData = (savedLayer: LayerBase & LayerStyleProperties): { layerBase: LayerBase, layerStyle: LayerStyleProperties } => {
    const layerBase: Partial<LayerBase> = {};
    const layerStyle: Partial<LayerStyleProperties> = {};

    // Proprietà note di LayerBase (mantenere aggiornato con src/store/types.ts)
    const knownLayerBaseKeys: (keyof LayerBase)[] = [
      'id', 'name', 'data', 'visible', 'maplibreLayerType', 'availablePropertyKeys',
      'originalFormat', 'format', 'maplibreSourceId', 'maplibreLayerId', 'extent',
      'minzoom', 'maxzoom', 'filter', 'sourceSpecification', 'sourceLayer', 'sourceType'
    ];

    for (const key in savedLayer) {
      if (Object.prototype.hasOwnProperty.call(savedLayer, key)) {
        if (knownLayerBaseKeys.includes(key as keyof LayerBase)) {
          (layerBase as any)[key] = (savedLayer as any)[key];
        } else {
          // Se non è una chiave di LayerBase, si presume sia di LayerStyleProperties
          (layerStyle as any)[key] = (savedLayer as any)[key];
        }
      }
    }

    // Logica di fallback per sourceType (come nell'originale, ora centralizzata qui)
    let sourceTypeToSet = layerBase.sourceType;
    if (!sourceTypeToSet) {
      if (layerBase.maplibreLayerType === 'raster') {
        sourceTypeToSet = 'raster';
      } else if (layerBase.data && (
          layerBase.maplibreLayerType === 'fill' ||
          layerBase.maplibreLayerType === 'line' ||
          layerBase.maplibreLayerType === 'circle' ||
          layerBase.maplibreLayerType === 'symbol' ||
          layerBase.maplibreLayerType === 'heatmap' ||
          layerBase.maplibreLayerType === 'fill-extrusion'
      )) {
        sourceTypeToSet = 'geojson';
      }
      // Altri casi potrebbero essere aggiunti se necessario
    }
    layerBase.sourceType = sourceTypeToSet;

    return {
      layerBase: layerBase as LayerBase,
      layerStyle: layerStyle as LayerStyleProperties
    };
  };

  return {
    // Stato UI Pannelli
    isLeftPanelCollapsed: false,
  isRightPanelOpen: false,
  rightPanelContext: null,
  selectedLayerIdForPanel: null,

  // Azioni UI Pannelli
  toggleLeftPanel: () => set(state => ({ isLeftPanelCollapsed: !state.isLeftPanelCollapsed })),
  openRightPanel: (context: 'general' | 'layer', layerId?: string) => set({
    isRightPanelOpen: true,
    rightPanelContext: context,
    selectedLayerIdForPanel: context === 'layer' ? layerId ?? null : null, // Imposta layerId solo se il contesto è 'layer'
  }),
  closeRightPanel: () => set({
    isRightPanelOpen: false,
    rightPanelContext: null,
    selectedLayerIdForPanel: null,
  }),

  // Azioni Salva/Carica Sessione (Non salviamo/carichiamo lo stato del pannello destro per ora)
  loadSessionState: (newState: SavedState) => {
    // Reidratazione Bounds
    let hydratedBounds: LngLatBounds | null = null;
    if (newState.mapViewState?.bounds?._sw && newState.mapViewState?.bounds?._ne) {
      try {
        hydratedBounds = new LngLatBounds(
          newState.mapViewState.bounds._sw,
          newState.mapViewState.bounds._ne
        );
      } catch (error) {
        console.error("Failed to rehydrate LngLatBounds during session load:", error);
        hydratedBounds = null;
      }
    }

    // Prepara lo stato da impostare usando 'set' globale
    const stateToSet: Partial<ReturnType<typeof get>> = {};

    // 1. Map View State
    if (newState.mapViewState) {
      stateToSet.mapState = {
        latitude: newState.mapViewState.latitude ?? get().mapState.latitude, // Aggiunto
        longitude: newState.mapViewState.longitude ?? get().mapState.longitude, // Aggiunto
        zoom: newState.mapViewState.zoom ?? get().mapState.zoom,
        pitch: newState.mapViewState.pitch ?? get().mapState.pitch,
        bearing: newState.mapViewState.bearing ?? get().mapState.bearing,
        bounds: hydratedBounds ?? get().mapState.bounds, // Usa bounds reidratato o quello esistente
      };
    }

    // 2. Basemap (Usa activeBasemapId)
    if (newState.activeBasemapId) { // Modificato da activeBasemapUrl
      stateToSet.activeBasemapId = newState.activeBasemapId; // Modificato da currentBasemapUrl
    }

    // 3. Layers e Layer Styles
    if (newState.layers) {
      const loadedLayers: LayerBase[] = [];
      const loadedStyles: Record<string, LayerStyleProperties> = {};

      newState.layers.forEach(savedLayer => {
        // Utilizza la nuova funzione di utilità per separare le proprietà
        // Si assume che savedLayer sia del tipo (LayerBase & LayerStyleProperties)
        // come definito in SavedState.layers
        const { layerBase, layerStyle } = splitSavedLayerData(savedLayer);

        loadedLayers.push(layerBase);
        if (layerBase.id) { // Assicurati che l'ID esista prima di usarlo come chiave
          loadedStyles[layerBase.id] = layerStyle;
        } else {
          console.warn("Layer caricato senza ID, impossibile associare stili:", savedLayer);
        }
      });
      stateToSet.dataLayers = loadedLayers;
      stateToSet.layerStyles = loadedStyles;
    }

    // 4. Attribute Tables
    if (newState.openAttributeTables) {
      stateToSet.openAttributeTableIds = newState.openAttributeTables;
    }

    // 5. Map Controls (escluso terrain/hillshade)
    if (newState.mapControls) {
        stateToSet.isFullscreenControlActive = newState.mapControls.isFullscreenControlActive ?? get().isFullscreenControlActive;
        stateToSet.isGeolocateControlActive = newState.mapControls.isGeolocateControlActive ?? get().isGeolocateControlActive;
        stateToSet.isNavigationControlActive = newState.mapControls.isNavigationControlActive ?? get().isNavigationControlActive;
        stateToSet.isScaleControlActive = newState.mapControls.isScaleControlActive ?? get().isScaleControlActive;
        stateToSet.isGlobeControlActive = newState.mapControls.isGlobeControlActive ?? get().isGlobeControlActive;
    }

    // Applica lo stato aggregato
    set(stateToSet);

    // Dopo aver caricato tutti i layer del progetto, triggera lo zoom sull'ultimo layer aggiunto
    const lastLoadedLayerId = stateToSet.dataLayers?.[stateToSet.dataLayers.length - 1]?.id;
    if (lastLoadedLayerId) {
      get().zoomToLayerExtent(lastLoadedLayerId);
    }

    // Potremmo voler resettare lo stato della left-panel al caricamento, o salvarlo/caricarlo
    // Per ora, lo lasciamo invariato al caricamento della sessione.
    // Se volessimo salvarlo/caricarlo, dovremmo aggiungerlo a SavedState e a getStateForSave.
  },

  getStateForSave: () => {
    const state = get();
    const layersToSave = state.dataLayers.map(layer => ({
      ...layer, // Include le proprietà di LayerBase
      ...(state.layerStyles[layer.id] || {}), // Aggiunge le proprietà di stile corrispondenti
    }));

    // Non è più necessario rinominare symbolSortKey o creare symbolSortKeyLayout
    // layersToSave.forEach((layer: any) => {
    //     // Gestisci anche fillExtrusionSortKey se necessario
    //     // if (layer.fillExtrusionSortKey !== undefined) { ... }
    // });


    // Prepara mapViewState per il salvataggio, serializzando i bounds
    const currentMapState = state.mapState;
    const mapViewStateForSave: SavedState['mapViewState'] = {
      latitude: currentMapState.latitude,
      longitude: currentMapState.longitude,
      zoom: currentMapState.zoom,
      pitch: currentMapState.pitch,
      bearing: currentMapState.bearing,
      // Non includere 'bounds' qui inizialmente se è LngLatBounds
    };

    if (currentMapState.bounds) {
      mapViewStateForSave.bounds = {
        _sw: (currentMapState.bounds as any)._sw,
        _ne: (currentMapState.bounds as any)._ne,
      };
    } else {
      mapViewStateForSave.bounds = undefined;
    }

    return {
      version: "1.0.0", // Versione dello stato salvato
      mapViewState: mapViewStateForSave, // Usa la versione serializzata
      activeBasemapId: state.activeBasemapId, // Modificato da activeBasemapUrl e currentBasemapUrl
      layers: layersToSave,
      openAttributeTables: state.openAttributeTableIds,
      mapControls: {
        isFullscreenControlActive: state.isFullscreenControlActive,
        isGeolocateControlActive: state.isGeolocateControlActive,
        isNavigationControlActive: state.isNavigationControlActive,
        isScaleControlActive: state.isScaleControlActive,
        isGlobeControlActive: state.isGlobeControlActive,
        // Escludi terrain/hillshade come da logica originale
      },
    };
  },
};
};